import { Box } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import SystemDialog from "../../../../components/UIToolkit/Popups/Dialog";

type Props = {
	open: boolean;
	firstName: string;
	jobNames: string[];
	/** Empty when the admin cleared every job, leaving nothing to move shifts onto. */
	newJobName: string;
	onClose: () => void;
	onApply: () => void;
	applyLoading: boolean;
};

/**
 * Shown when an admin removes a job from a nurse who still has future
 * scheduled or recurring shifts under that job. The admin picks between
 * clearing those shifts by hand and moving them onto the new primary job
 * (DAT-582) — the latter finishes the whole job change in one click.
 *
 * When the nurse has no future shifts/recurring on the removed job, no dialog
 * appears at all — `useProfileV2State` saves directly via `PUT /jobs`, which
 * silently cancels any pending pickups on the removed jobs.
 */
const JobChangeDialog: FC<Props> = ({ open, firstName, jobNames, newJobName, onClose, onApply, applyLoading }) => {
	const { t } = useTranslation();
	const canApply = !!newJobName;

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			dataTestId="job-change-blocked-dialog"
			title={t("jobChange.blocked.title", { jobName: jobNames.join(", ") })}
			body={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, maxWidth: 460 }}>
					<Box>{t("jobChange.blocked.body", { firstName })}</Box>
					<Box sx={{ fontWeight: 600 }}>{canApply ? t("jobChange.blocked.action", { newJobName }) : t("jobChange.blocked.actionNoNewJob")}</Box>
				</Box>
			}
			hideCancel={!canApply}
			cancel={t("jobChange.blocked.ok")}
			confirm={canApply ? t("jobChange.blocked.apply") : t("jobChange.blocked.ok")}
			confirmButtonVariant={canApply ? "primary" : "borderless"}
			confirmLoading={applyLoading}
			onConfirm={canApply ? onApply : onClose}
		/>
	);
};

export default JobChangeDialog;
